import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageSquare, Loader2, RefreshCw, CheckCircle2, XCircle, Send, Check } from 'lucide-react';

import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const PURPOSES = [
  { value: 'all', label: 'All messages' },
  { value: 'counseling_slip', label: 'Counseling slips' },
  { value: 'stock_digest', label: 'Stock / expiry digests' },
  { value: 'receipt', label: 'Sale receipts' },
  { value: 'test', label: 'Test messages' },
  { value: 'general', label: 'Other' },
];

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const Stat = ({ label, value, tone = '', testid }) => (
  <div className="rounded-lg border p-3" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

export default function SmsLogPage() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ logs: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [purpose, setPurpose] = useState('all');
  const [days, setDays] = useState('30');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: Number(days), limit: 200 };
      if (status !== 'all') params.status = status;
      if (purpose !== 'all') params.purpose = purpose;
      const { data: res } = await api.get('/sms/logs', { params });
      setData(res);
    } catch {
      setData({ logs: [], summary: {} });
    } finally {
      setLoading(false);
    }
  }, [status, purpose, days]);

  useEffect(() => { load(); }, [load]);

  const resend = async (row) => {
    setBusyId(row.log_id);
    try {
      await api.post(`/sms/logs/${row.log_id}/resend`);
      toast.success(`Resent to ${row.to_masked}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setBusyId(null);
    }
  };

  const acknowledge = async (row) => {
    setBusyId(row.log_id);
    try {
      await api.post(`/sms/logs/${row.log_id}/acknowledge`);
      toast.success('Marked as handled — the alert is cleared');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear that alert');
    } finally {
      setBusyId(null);
    }
  };

  const s = data.summary || {};

  return (
    <div className="space-y-6" data-testid="sms-log-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> SMS delivery log
          </h1>
          <p className="text-sm text-muted-foreground">
            Every text this pharmacy has attempted to send — counseling slips, digests, receipts — and whether it landed.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="sms-log-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Attempted" value={s.total ?? 0} testid="sms-stat-total" />
        <Stat label="Delivered to Arkesel" value={s.sent ?? 0} tone="text-emerald-600" testid="sms-stat-sent" />
        <Stat label="Failed" value={s.failed ?? 0} tone={s.failed ? 'text-rose-600' : ''} testid="sms-stat-failed" />
        <Stat label="Last 24 hours" value={s.last_24h ?? 0} testid="sms-stat-24h" />
      </div>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Messages</CardTitle>
              <CardDescription>Recipient numbers are part-hidden to protect patient privacy.</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-[140px]" data-testid="sms-log-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All outcomes</SelectItem>
                  <SelectItem value="sent">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger className="w-[190px]" data-testid="sms-log-purpose-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PURPOSES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-[130px]" data-testid="sms-log-days-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : data.logs.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="sms-log-empty">
              No SMS attempts recorded in this period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.logs.map((row) => (
                    <TableRow key={row.log_id} data-testid={`sms-log-row-${row.log_id}`}>
                      <TableCell className="whitespace-nowrap text-xs">{when(row.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{row.to_masked}</TableCell>
                      <TableCell className="text-xs">{row.purpose_label}</TableCell>
                      <TableCell>
                        {row.ok ? (
                          <Badge variant="outline" className="gap-1 text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                            <CheckCircle2 className="h-3 w-3" /> Delivered
                          </Badge>
                        ) : (
                          <div className="space-y-0.5">
                            <Badge variant="outline" className="gap-1 text-xs bg-rose-500/10 text-rose-600 border-rose-500/30">
                              <XCircle className="h-3 w-3" /> Failed
                            </Badge>
                            <p className="text-[11px] text-muted-foreground max-w-[220px]">{row.error}</p>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[320px] truncate" title={row.preview}>
                        {row.preview}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {row.ok ? null : row.resolved_at ? (
                          <span className="text-[11px] text-muted-foreground" data-testid={`sms-log-resolved-${row.log_id}`}>
                            {row.resolution === 'acknowledged' ? 'Handled' : row.resolution === 'retried' ? 'Retried' : 'Resent'}
                          </span>
                        ) : (
                          <div className="flex gap-1.5 justify-end">
                            {row.can_resend && (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                disabled={busyId === row.log_id} onClick={() => resend(row)}
                                data-testid={`sms-log-resend-${row.log_id}`}>
                                {busyId === row.log_id
                                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  : <Send className="h-3 w-3 mr-1" />}
                                Retry
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                              disabled={busyId === row.log_id} onClick={() => acknowledge(row)}
                              title="Clear the alert — handled another way"
                              data-testid={`sms-log-ack-${row.log_id}`}>
                              <Check className="h-3 w-3 mr-1" /> Handled
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
